import React, { useEffect, useState } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DETAIL_DISPATCH_NOTE, CREATE_DISPATCH_NOTE, EDIT_DISPATCH_NOTE } from "../../../routes";
import {
  GenericTable,
  Pagination,
  RoleValidator,
  SearchableDropdown,
} from "../../../Components/Common";
import { useDispatchNote } from "../Hooks/useDispatchNote";
import { useWarehouse } from "../Hooks/useWarehouse";
import { useMRR } from "../Hooks/useMRR";
import {
  getDNStatusColor,
  DISPATCH_NOTE_COLUMNS,
  DISPATCH_NOTE_LABELS,
  DISPATCH_NOTE_STATUS_LABEL,
  DISPATCH_NOTE_STATUS
} from "../constants";
import { MdModeEditOutline } from "react-icons/md";
import { ROLES } from "../../../Constants/Strings";
import { formatDateTime } from "../../../Utils/index";

const DispatchNoteList = () => {
  const {
    loading,
    filters,
    page,
    onPageChange,
    onPageSizeChange,
    totalPages,
    rowsPerPage,
    setFilters,
    fetchDispatchNotes,
    deleteDispatchNoteById
  } = useDispatchNote();
  const {
    fetchWarehouses
  } = useWarehouse();
  const {
    fetchAllMRRs
  } = useMRR();
  const [dispatchNotes, setDispatchNotes] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    const data = await fetchDispatchNotes();
    if (data) setDispatchNotes(data);
  };

  const deleteNote = async (dn_id) => {
    await deleteDispatchNoteById(dn_id);
    await fetchData()
  };

  const renderField = (column, row) => {
    switch (column) {
      case "status":
        return <div className={`py-1 px-2 font-semibold w-fit ${getDNStatusColor(row.status)}  rounded-md`}>{DISPATCH_NOTE_STATUS_LABEL[row.status] || "N/A"}</div>;
      case "warehouse":
        return row.warehouse?.name || "N/A";
      case "created":
        return formatDateTime(row.created) || "N/A";
      case "actions":
        return (
          <div className="flex items-center gap-2">
            {/* View */}
            <button className=" hover:underline ml-2">
              <Info
                className="h-5 w-5 text-blue-400"
                onClick={() => navigate(DETAIL_DISPATCH_NOTE.replace(":instanceId", row.id))}
              />
            </button>
            
            {/* Edit */}
            <RoleValidator allowedRoles={row.status === DISPATCH_NOTE_STATUS.DRAFT ? [ROLES.INVENTORY_INCHARGE] : []}>
              <button className=" hover:underline ml-2">
                <MdModeEditOutline
                  className="h-5 w-5 text-blue-400"
                  onClick={() => navigate(EDIT_DISPATCH_NOTE.replace(":instanceId", row.id))}
                />
              </button>
            </RoleValidator>

            {/* Delete */}
            <RoleValidator allowedRoles={row.status === DISPATCH_NOTE_STATUS.DRAFT ? [ROLES.INVENTORY_INCHARGE] : []}>
              <button className="p-1 hover:bg-red-100 rounded-lg transition-colors" onClick={() => { deleteNote(row.id) }}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            </RoleValidator>
          </div>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const renderMobileCard = (row) => (
    <div className="p-2">
      <div className="bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden relative">
        <div className="p-4 flex flex-col space-y-2 w-[80%]">
          <div className="text-lg font-semibold text-gray-800 truncate">
            {row.dn_code || "N/A"}
          </div>
          <div className="text-gray-600 text-sm truncate">
            {row.warehouse_name || "N/A"}
          </div>
          <div className="text-gray-600 text-sm truncate">
            {row.mrr_code || "N/A"}
          </div>
        </div>

        <div className="absolute top-3 right-3 flex space-x-3">
          {/* View */}
          <button
            onClick={() => navigate(DETAIL_DISPATCH_NOTE.replace(":instanceId", row.id))}
            className="p-1 rounded-lg"
          >
            <Info className="h-4 w-4 text-blue-400" />
          </button>

          {/* Edit */}
          <RoleValidator allowedRoles={row.status === DISPATCH_NOTE_STATUS.DRAFT ? [ROLES.INVENTORY_INCHARGE] : []}>
            <button
              onClick={() => navigate(EDIT_DISPATCH_NOTE.replace(":instanceId", row.id))}
              className="p-1 rounded-lg"
            >
              <MdModeEditOutline className="h-4 w-4 text-blue-500" />
            </button>
          </RoleValidator>

          {/* Delete */}
          <RoleValidator allowedRoles={row.status === DISPATCH_NOTE_STATUS.DRAFT ? [ROLES.INVENTORY_INCHARGE] : []}>
            <button className="p-1 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" onClick={() => { deleteNote(row.id) }}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </RoleValidator>
        </div>

        <div className={`absolute bottom-3 right-4 bg-gray-100 px-3 py-1 rounded-full text-sm font-medium text-gray-800 shadow-inner ${getDNStatusColor(
          row.status
        )}`}>
          {DISPATCH_NOTE_STATUS_LABEL[row.status] || "N/A"}
        </div>
      </div>
    </div >
  );

  useEffect(() => {
    fetchData();
  }, [filters, page, totalPages, rowsPerPage]);

  return (
    <div className="py-6 px-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4">
          Dispatch Notes Management
        </h1>
        <RoleValidator allowedRoles={[ROLES.INVENTORY_INCHARGE]}>
          <button
            className="mb-4 flex gap-1 px-4 py-2 bg-primary text-white rounded hover:bg-gray-700 transition"
            onClick={() => navigate(CREATE_DISPATCH_NOTE)}
          >
            <Plus />
            <span className="hidden md:block">Add Dispatch Note</span>
          </button>
        </RoleValidator>
      </div>

      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3">
        <div className="flex gap-2">
          <SearchableDropdown
            fetchFunction={fetchWarehouses}
            onSelect={(val) => setFilters({ ...filters, warehouse: val })}
            valueKey="id"
            labelKey="name"
            includeAllOption
            allOptionLabel="All Warehouses"
            placeholder={"Select Warehouse"}
            width="64"
          />
          <SearchableDropdown
            fetchFunction={fetchAllMRRs}
            onSelect={(val) => setFilters({ ...filters, mrr: val })}
            valueKey="id"
            labelKey="mrr_code"
            includeAllOption
            allOptionLabel="All MRRs"
            placeholder={"Select MRR"}
            width="64"
          />
        </div>
      </div>
      <div className="py-2">
        <div className="h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="h-full w-full flex justify-center items-center">
              <div className=" animate-spin border-t border-blue-600"></div>
            </div>
          ) : (
            <GenericTable
              tableColumns={DISPATCH_NOTE_COLUMNS}
              tableRows={dispatchNotes}
              columnLabels={DISPATCH_NOTE_LABELS}
              renderField={renderField}
              renderMobileCard={renderMobileCard}
            />
          )}
        </div>
        <div className="shrink-0 mt-2">
          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>

  );
};

export default DispatchNoteList;
